import { Tooltip } from "antd";
import { ReactElement } from "react";
import { Nullable } from "src/common/utils/nullable";

enum ValuesEnum {
  Some = "Some",
  All = "All",
  None = "None",
}

interface Values {
  type: ValuesEnum;
  values: Nullable<Array<string>>;

  render(isGlobal: boolean): ReactElement;
}

class Some implements Values {
  type: ValuesEnum.Some = ValuesEnum.Some;
  values: Array<string>;

  constructor(input: Values) {
    this.values = input.values ?? [];
  }

  render(_isGlobal: boolean): ReactElement {
    return <>{this.values.join(", ")}</>;
  }
}

class All implements Values {
  type: ValuesEnum.All = ValuesEnum.All;
  values: Nullable<Array<string>> = null;

  constructor(allValues: Array<string>) {
    this.values = allValues;
  }

  render(isGlobal: boolean): ReactElement {
    return (
      <>
        {isGlobal ? (
          "--"
        ) : this.values !== null && this.values.length > 0 ? (
          <Tooltip title={<>{this.values?.join(", ")}</>}>
            <div className="all-values-defined">{this.type}</div>
          </Tooltip>
        ) : (
          <div className="all-values-undefined">{this.type}</div>
        )}
      </>
    );
  }
}

class None implements Values {
  type: ValuesEnum.None = ValuesEnum.None;
  values: Nullable<Array<string>> = null;

  render(): ReactElement {
    return <>{this.type}</>;
  }
}

function initializeValues(input: Nullable<Values>, allValues: Array<string>): Values {
  if (input === null) {
    return new None();
  }
  if (input.type === ValuesEnum.All) {
    return new All(allValues);
  } else if (input.type === ValuesEnum.None) {
    return new None();
  } else if (input.type === ValuesEnum.Some) {
    return new Some(input);
  } else {
    return new None();
  }
}

enum ScopeEnum {
  Country = "country",
  Region = "region",
  Account = "account",
  Global = "global",
  Empty = "empty-scope",
}

export interface Scope {
  name: ScopeEnum;
  values: Nullable<Values>;
}

class Global implements Scope {
  name: ScopeEnum.Global = ScopeEnum.Global;
  values: Values = new All([]);
}

class Region implements Scope {
  name: ScopeEnum.Region = ScopeEnum.Region;
  values: Values;
  constructor(input: Scope, allRegions: Array<string>) {
    this.values = initializeValues(input.values, allRegions);
  }
}

class Country implements Scope {
  name: ScopeEnum.Country = ScopeEnum.Country;
  values: Values;
  constructor(input: Scope, allCountries: Array<string>) {
    this.values = initializeValues(input.values, allCountries);
  }
}

class Account implements Scope {
  name: ScopeEnum.Account = ScopeEnum.Account;
  values: Values;
  constructor(input: Scope) {
    this.values = initializeValues(input.values, []);
  }
}

class Empty implements Scope {
  name: ScopeEnum.Empty = ScopeEnum.Empty;
  values: Values = new None();
}

export function initializeScope(
  input: Scope,
  geographies: { allCountries: Array<string>; allRegions: Array<string> }
): Scope {
  if (input.name === ScopeEnum.Global) {
    return new Global();
  } else if (input.name === ScopeEnum.Region) {
    return new Region(input, geographies.allRegions);
  } else if (input.name === ScopeEnum.Country) {
    return new Country(input, geographies.allCountries);
  } else if (input.name === ScopeEnum.Account) {
    return new Account(input);
  } else {
    return new Empty();
  }
}

export function isGlobalScope(scope: Scope): boolean {
  return scope.name === ScopeEnum.Global;
}
